/**
 * GraphRAG Dashboard
 *
 * Search bar + full-width knowledge graph explorer.
 * Takes natural language questions, generates Cypher via LLM, renders graph results.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { RefreshCw } from 'lucide-react';

import graphragService from './services/graphragService';
import QueryInterface from './components/QueryInterface';
import GraphExplorerPro from './components/GraphExplorerPro';
import SuggestionsPanel from './components/SuggestionsPanel';

const GraphRAGDashboard = () => {
  // Auth and tenant
  const { getAccessTokenSilently, user } = useAuth0();
  const tenantName = user?.['https://trustx.cloud/claims/company'];

  // Navigation state (for initial query from Overview page)
  const location = useLocation();
  const navigate = useNavigate();
  const initialQueryConsumed = useRef(false);

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Loading & data states
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [activeQuery, setActiveQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [starterSuggestions, setStarterSuggestions] = useState([]);
  const [hasQueried, setHasQueried] = useState(false);
  const [recentQueries, setRecentQueries] = useState([]);

  // Expand tracking
  const [expandedNodeIds, setExpandedNodeIds] = useState(new Set());
  const [expanding, setExpanding] = useState(false);

  // UI states
  const [error, setError] = useState(null);

  // Initialize service on mount
  useEffect(() => {
    const initService = async () => {
      if (!tenantName) {
        console.error('Missing tenant claim in JWT token');
        setError('Unable to determine your organization. Please contact support if this persists.');
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        graphragService.setAuthToken(token);

        const newSessionId = graphragService.generateSessionId();
        setSessionId(newSessionId);
        setIsInitialized(true);

        // Fetch tenant-specific starter suggestions
        try {
          const res = await graphragService.getStarterSuggestions();
          if (res.success && res.data?.suggestions) {
            setStarterSuggestions(res.data.suggestions);
          }
        } catch (sugErr) {
          console.log('Could not fetch starter suggestions:', sugErr);
        }
      } catch (err) {
        console.error('Failed to initialize GraphRAG service:', err);
        setError('Failed to initialize. Please refresh the page.');
      }
    };

    initService();
  }, [getAccessTokenSilently, tenantName]);

  /**
   * Submit a query to GraphRAG
   */
  const handleSubmitQuery = useCallback(async (query) => {
    if (!query.trim() || loading) return;

    setHasQueried(true);
    setError(null);
    setLoading(true);
    setActiveQuery(query.trim());

    // Track recent queries (deduplicate, max 10)
    setRecentQueries(prev => {
      const filtered = prev.filter(q => q !== query.trim());
      return [query.trim(), ...filtered].slice(0, 10);
    });

    try {
      const response = await graphragService.submitQueryV2(query, sessionId);

      if (response.success) {
        const data = response.data;

        // Update graph data if available (full replace for NL queries)
        if (data.graph_data && data.graph_data.nodes?.length > 0) {
          setGraphData(data.graph_data);
          setExpandedNodeIds(new Set());
        } else {
          setGraphData(null);
          setExpandedNodeIds(new Set());
        }

        // Use inline suggestions from the response
        if (data.suggestions?.length > 0) {
          setSuggestions(data.suggestions);
        } else {
          setSuggestions([]);
        }
      } else {
        throw new Error(response.error || 'Query failed');
      }
    } catch (err) {
      console.error('Query error:', err);
      setError(err.message || 'Failed to process query');
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId]);

  /**
   * Merge new graph data into existing graph (for node expansion).
   */
  const mergeGraphData = useCallback((existing, newData) => {
    if (!existing) return newData;
    if (!newData) return existing;

    const linkId = (v) => (typeof v === 'object' && v !== null) ? v.id : v;

    const existingNodeIds = new Set((existing.nodes || []).map(n => n.id));
    const existingLinkKeys = new Set(
      (existing.links || []).map(l =>
        `${linkId(l.source)}->${l.type || l.relationship}->${linkId(l.target)}`
      )
    );

    const mergedNodes = [...existing.nodes];
    for (const node of newData.nodes || []) {
      if (!existingNodeIds.has(node.id)) {
        existingNodeIds.add(node.id);
        mergedNodes.push(node);
      }
    }

    const mergedLinks = (existing.links || []).map(l => ({
      ...l,
      source: linkId(l.source),
      target: linkId(l.target),
    }));
    for (const link of newData.links || []) {
      const src = linkId(link.source);
      const tgt = linkId(link.target);
      const key = `${src}->${link.type || link.relationship}->${tgt}`;
      if (!existingLinkKeys.has(key)) {
        existingLinkKeys.add(key);
        mergedLinks.push({ ...link, source: src, target: tgt });
      }
    }

    return { nodes: mergedNodes, links: mergedLinks };
  }, []);

  /**
   * Expand a node's neighbors and merge into existing graph.
   */
  const handleExpandNode = useCallback(async (node) => {
    if (expanding) return;

    const nodeType = node.type || node.entity_type;
    const nodeId = node.id || node.email || node.displayName || node.name;

    if (!nodeId || !nodeType) return;

    setExpanding(true);
    setError(null);

    try {
      const response = await graphragService.expandNode(nodeId, nodeType);

      if (response.success && response.data?.graph_data) {
        const newGraphData = response.data.graph_data;
        if (newGraphData.nodes?.length > 0) {
          setGraphData(prev => {
            const existingIds = new Set((prev?.nodes || []).map(n => n.id));
            for (const n of newGraphData.nodes) {
              if (!existingIds.has(n.id)) {
                n.x = (node.x || 0) + (Math.random() - 0.5) * 100;
                n.y = (node.y || 0) + (Math.random() - 0.5) * 100;
              }
            }
            return mergeGraphData(prev, newGraphData);
          });
          setExpandedNodeIds(prev => new Set([...prev, nodeId]));
        }
      } else {
        console.warn('Expand returned no data:', response);
      }
    } catch (err) {
      console.error('Expand error:', err);
    } finally {
      setExpanding(false);
    }
  }, [expanding, mergeGraphData]);

  // Auto-execute initial query passed from Overview page via navigation state
  useEffect(() => {
    const initialQuery = location.state?.initialQuery;
    if (isInitialized && initialQuery && !initialQueryConsumed.current) {
      initialQueryConsumed.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      handleSubmitQuery(initialQuery);
    }
  }, [isInitialized, location.state, location.pathname, navigate, handleSubmitQuery]);

  /**
   * Handle suggestion click
   */
  const handleSuggestionClick = (suggestion) => {
    handleSubmitQuery(suggestion);
  };

  /**
   * Handle node click -- update suggestions contextually based on node type
   */
  const handleNodeClick = (node) => {
    const nodeType = node.type || node.entity_type;
    const contextSuggestions = [];

    if (nodeType === 'User' && node.email) {
      contextSuggestions.push(
        `What groups does ${node.email} belong to?`,
        `What applications does ${node.email} have access to?`,
        `Show me the full graph for ${node.email}`,
        `Does ${node.email} have privilege creep?`,
      );
    } else if (nodeType === 'Group' && node.name) {
      contextSuggestions.push(
        `Show me members of the group "${node.name}"`,
        `Who has admin access in "${node.name}"?`,
        `Show me all groups`,
      );
    } else if (nodeType === 'Application' && (node.name || node.displayName)) {
      const appName = node.displayName || node.name;
      contextSuggestions.push(
        `Who has access to "${appName}"?`,
        `Show me all applications`,
      );
    } else if (nodeType === 'Repository' && (node.name || node.displayName)) {
      const repoName = node.displayName || node.name;
      contextSuggestions.push(
        `Who has access to the repository "${repoName}"?`,
        `Show me all resources hosted by GitHub Enterprise Cloud`,
      );
    } else if (nodeType === 'JiraProject' && (node.name || node.displayName)) {
      const projName = node.displayName || node.name;
      contextSuggestions.push(
        `Who has access to the Jira project "${projName}"?`,
        `Show me all resources hosted by Jira`,
      );
    } else if (nodeType === 'Department' && node.name) {
      contextSuggestions.push(
        `Show me all users in ${node.name} department`,
        `Show me access patterns for ${node.name}`,
      );
    } else if (nodeType === 'PrivilegeCreepInsight') {
      contextSuggestions.push(
        'Show me all users with privilege creep',
        'Who has the highest privilege creep score?',
        'Show me users with excessive permissions',
      );
    } else if (nodeType === 'DormantUserInsight') {
      contextSuggestions.push(
        'Show me all dormant users',
        'Who has been inactive the longest?',
        'Show me dormant users with admin access',
      );
    } else if (nodeType === 'ExcessiveAccessInsight') {
      contextSuggestions.push(
        'Show me users with excessive access',
        'Who has the most access compared to peers?',
        'Find users with excessive permissions',
      );
    }

    if (contextSuggestions.length > 0) {
      setSuggestions(contextSuggestions);
    }
  };

  /**
   * Clear graph and start new session
   */
  const handleNewSession = async () => {
    const newSessionId = graphragService.generateSessionId();
    setSessionId(newSessionId);
    setGraphData(null);
    setActiveQuery('');
    setSuggestions([]);
    setHasQueried(false);
    setError(null);
    setExpandedNodeIds(new Set());

    try {
      const res = await graphragService.getStarterSuggestions();
      if (res.success && res.data?.suggestions) {
        setStarterSuggestions(res.data.suggestions);
      }
    } catch (err) {
      console.log('Could not refetch starter suggestions:', err);
    }
  };

  // Render loading state
  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 56px)',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Initializing GraphRAG...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 56px)',
        overflow: 'hidden',
      }}
    >
      {/* Search Bar Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <QueryInterface
            onSubmit={handleSubmitQuery}
            loading={loading}
            placeholder="Ask about users, groups, access patterns..."
            recentQueries={recentQueries}
            onSelectRecent={handleSubmitQuery}
          />
        </Box>
        <Tooltip title="New session">
          <IconButton
            size="small"
            onClick={handleNewSession}
            sx={{
              border: 1,
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <RefreshCw size={16} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Starter suggestions (before first query) */}
      {!hasQueried && starterSuggestions.length > 0 && !loading && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <SuggestionsPanel
            suggestions={starterSuggestions}
            onSuggestionClick={handleSuggestionClick}
            variant="starter"
          />
        </Box>
      )}

      {/* Contextual / follow-up suggestions (after query or node click) */}
      {hasQueried && suggestions.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <SuggestionsPanel
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
          />
        </Box>
      )}

      {/* Error alert */}
      {error && (
        <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Active query label */}
      {activeQuery && !loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.5,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing results for:
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {activeQuery}
          </Typography>
        </Box>
      )}

      {/* Full-width graph */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <GraphExplorerPro
          data={graphData}
          loading={loading || expanding}
          noResults={hasQueried && !loading && !graphData && !error}
          noResultsSuggestions={suggestions}
          onSuggestionClick={handleSuggestionClick}
          onNodeClick={handleNodeClick}
          onQueryNode={handleExpandNode}
          expandedNodeIds={expandedNodeIds}
          graphragService={graphragService}
          tenantId={tenantName || 'trustx-demo'}
        />
      </Box>
    </Box>
  );
};

export default GraphRAGDashboard;
